import { FormEvent, useRef, useState } from "react"
import { usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { MessageSquareWarning, Paperclip, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { useFeedback } from "@/components/feedback/FeedbackProvider"
import { csrfToken } from "@/lib/csrf"
import { shrinkImage } from "@/lib/image"
import type { PageProps } from "@/types/inertia"
import { FEEDBACK_CATEGORIES, FEEDBACK_CATEGORY_MESSAGES, type FeedbackCategory } from "@/types/feedback"

const MAX_LENGTH = 5000
const MAX_SCREENSHOT_BYTES = 3 * 1024 * 1024

type Status = "editing" | "sending" | "sent"

/**
 * The feedback dialog, mounted once alongside every page (see lib/render-page).
 * Pages with a nav open it from a nav item; other signed-in pages get a floating button.
 * Signed-out visitors get neither.
 * Posts with fetch() to a JSON endpoint, so it never disturbs the page's own
 * Inertia state.
 */
export function FeedbackWidget() {
  const intl = useIntl()
  const { open, setOpen, hasNavTrigger } = useFeedback()
  const signedIn = !!usePage<PageProps>().props.current_user
  const [message, setMessage] = useState("")
  const [category, setCategory] = useState<FeedbackCategory | "">("")
  const [screenshot, setScreenshot] = useState<File | null>(null)
  const [status, setStatus] = useState<Status>("editing")
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const reset = () => {
    setMessage("")
    setCategory("")
    setScreenshot(null)
    setStatus("editing")
  }

  const onOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next && status === "sent") reset()
    if (!next) setError(null)
  }

  const chooseScreenshot = async (file: File | undefined) => {
    if (!file) return
    setError(null)
    const shrunk = await shrinkImage(file)
    if (shrunk.size > MAX_SCREENSHOT_BYTES) {
      setError(intl.formatMessage({ id: "feedback.error_too_large" }))
      if (fileInput.current) fileInput.current.value = ""
      return
    }
    setScreenshot(shrunk)
  }

  const removeScreenshot = () => {
    setScreenshot(null)
    if (fileInput.current) fileInput.current.value = ""
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setStatus("sending")
    setError(null)

    const body = new FormData()
    body.append("feedback[message]", message)
    if (category) body.append("feedback[category]", category)
    body.append("feedback[page_path]", window.location.pathname + window.location.search)
    body.append("feedback[page_title]", document.title)
    if (screenshot) body.append("feedback[screenshot]", screenshot)

    try {
      // No Content-Type header: the browser sets the multipart boundary itself.
      const response = await fetch("/feedbacks", {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body,
      })
      if (response.ok) {
        setStatus("sent")
        return
      }
      const data = await response.json().catch(() => null)
      setError(data?.error ?? intl.formatMessage({ id: "feedback.error_generic" }))
    } catch {
      setError(intl.formatMessage({ id: "feedback.error_network" }))
    }
    setStatus("editing")
  }

  // Only signed-in people can send feedback; login, sign-up, and the like don't show it.
  if (!signedIn) return null

  return (
    <>
      {!hasNavTrigger && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-30 inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border border-hairline bg-page px-3 text-sm text-ink-body shadow-lg hover:bg-surface hover:text-ink-display focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <MessageSquareWarning className="h-4 w-4" aria-hidden="true" />
          {intl.formatMessage({ id: "feedback.floating" })}
        </button>
      )}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          {status === "sent" ? (
            <>
              <DialogHeader>
                <DialogTitle>{intl.formatMessage({ id: "feedback.thanks_title" })}</DialogTitle>
                <DialogDescription>{intl.formatMessage({ id: "feedback.thanks_body" })}</DialogDescription>
              </DialogHeader>
              <DialogFooter className="mt-6">
                <Button type="button" onClick={() => onOpenChange(false)}>
                  {intl.formatMessage({ id: "feedback.close" })}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={submit}>
              <DialogHeader>
                <DialogTitle>{intl.formatMessage({ id: "feedback.title" })}</DialogTitle>
                <DialogDescription>{intl.formatMessage({ id: "feedback.description" })}</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <fieldset className="space-y-2">
                  <legend>{intl.formatMessage({ id: "feedback.category_legend" })}</legend>
                  <RadioGroup orientation="horizontal" className="gap-x-4 gap-y-2">
                    {FEEDBACK_CATEGORIES.map((option) => (
                      <label key={option.value} className="flex cursor-pointer items-center gap-2 font-normal text-ink-body">
                        <Radio
                          name="feedback-category"
                          value={option.value}
                          checked={category === option.value}
                          onChange={() => setCategory(option.value)}
                        />
                        {intl.formatMessage(FEEDBACK_CATEGORY_MESSAGES[option.value])}
                      </label>
                    ))}
                  </RadioGroup>
                </fieldset>

                <div className="space-y-2">
                  <label htmlFor="feedback-message">{intl.formatMessage({ id: "feedback.message_label" })}</label>
                  <textarea
                    id="feedback-message"
                    className="form-control form-control-textarea"
                    required
                    autoFocus
                    maxLength={MAX_LENGTH}
                    rows={5}
                    aria-invalid={!!error}
                    aria-describedby={error ? "feedback-error" : undefined}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>


                <div>
                  <input
                    ref={fileInput}
                    id="feedback-screenshot"
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    className="sr-only"
                    onChange={(e) => chooseScreenshot(e.target.files?.[0])}
                  />
                  {screenshot ? (
                    <div className="flex items-center gap-2 text-sm">
                      <Paperclip className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
                      <span className="min-w-0 truncate">{screenshot.name}</span>
                      <button
                        type="button"
                        onClick={removeScreenshot}
                        className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display"
                        aria-label={intl.formatMessage({ id: "feedback.remove_screenshot" })}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <Button type="button" variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
                      <Paperclip className="h-4 w-4" /> {intl.formatMessage({ id: "feedback.attach" })}
                    </Button>
                  )}
                </div>

                {error && (
                  <p id="feedback-error" role="alert" className="text-xs text-danger-display">
                    {error}
                  </p>
                )}
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                  {intl.formatMessage({ id: "feedback.cancel" })}
                </Button>
                <Button type="submit" disabled={status === "sending" || message.trim() === ""}>
                  {intl.formatMessage(status === "sending" ? { id: "feedback.sending" } : { id: "feedback.send" })}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
